import { EllipsisOutlined } from '@ant-design/icons';
import { Dropdown, MenuProps } from 'antd';
import { ActionTag } from 'components/action-tag/action-tag-component';
import { BaseButton } from 'components/button/button-base';
import { uniq } from 'lodash';
import { useCallback, useState } from 'react';
import {
  BaseTableUserRecord,
  TableRow,
  UserListProps
} from '../user-table-types';

/**
 * Returns a memoized function that returns a list of actions that can be provided to a table column render function
 * @param props
 * @param reloadData
 * @returns
 */
export function useResolveActionsColumnData<T extends BaseTableUserRecord>(
  props: UserListProps<T>,
  reloadData: () => void
) {
  const [isLoading, setIsLoading] = useState<string[]>([]);
  return useCallback(
    (_value, record: TableRow<T>) => {
      const actions = props.actions ?? [];

      // Resolve which options are visible
      const visibleOptions = actions.filter(action => {
        // Do not show the tag if the tag should not be showing
        if (
          action.shouldShow === false ||
          (typeof action.shouldShow === 'function' &&
            !action.shouldShow(record))
        ) {
          return false;
        }
        return true;
      });

      // Convert each option into a tag
      const tags: MenuProps['items'] = visibleOptions.map((action, index) => {
        // Render the tag
        return {
          key: `action-${index}-${action.name}`,
          label: (
            <ActionTag
              action={{
                ...action,
                onClick: (...args) => {
                  setIsLoading(old => uniq([...old, record.key]));
                  return action.onClick(...args);
                },
                onSuccess: () => {
                  setIsLoading(old => old.filter(_ => _ !== record.key));
                  return action.onSuccess?.(reloadData);
                },
                onError: (...args) => {
                  setIsLoading(old => old.filter(_ => _ !== record.key));
                  // Invoke the original error handler
                  action.onError?.(...args);

                  // Invoke the higher-level error handler
                  props.onError?.(...args);
                }
              }}
              record={record}
            />
          )
        };
      });

      /**
       * Insert elements to the start of the list
       */
      tags.unshift(
        {
          key: 'header',
          label: 'Actions',
          disabled: true
        },
        {
          key: 'patientId',
          label: `User ID: ${record.raw.id}`,
          disabled: true
        }
      );

      return (
        <Dropdown
          menu={{
            items: tags
          }}
          trigger={['click']}
          arrow
        >
          <BaseButton loading={!!isLoading.find(_ => _ === record.key)}>
            <EllipsisOutlined />
          </BaseButton>
        </Dropdown>
      );
    },
    // eslint-disable-next-line
    [props.actions, reloadData, isLoading]
  );
}
